/**
 * /app/discovery — discovery runs + per-run results (read-only).
 *
 * Master/detail layout: runs list on the left, results table on the
 * right when a run is selected. Apply-result-to-slot is a follow-up
 * (atomic strategy-create + pair-replace + bot coordination).
 *
 * Design notes:
 *   * Legacy Discovery.tsx was 1657 lines with progress / logs /
 *     scheduler. We strip all of that — worker runs on cron, we only
 *     view results.
 *   * No "trigger run" button — worker schedules itself.
 *   * Results sorted by `rank` (worker's own ordering).
 */
import { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import {
  type DiscoveryResult,
  triggerDiscovery,
  useDiscoveryResults,
  useDiscoveryRuns,
  usePipelineLatest,
  useFleetHealth,
} from '@/hooks/useDashboardApi'
import { useAuthStore } from '@/stores/auth'
import { cn } from '@/lib/cn'
import { formatPct, formatUsd, timeAgo } from '@/lib/format'

export default function Discovery() {
  const { t } = useTranslation('app')
  const isAdmin = useAuthStore((s) => s.isAdmin())
  const qc = useQueryClient()
  const { data: runsData, isLoading: runsLoading } = useDiscoveryRuns(50)
  const [selectedRun, setSelectedRun] = useState<number | null>(null)

  // Auto-pick most-recent completed run on mount
  const initialRun = useMemo(() => {
    if (selectedRun != null) return selectedRun
    const first = runsData?.items.find((r) => r.status === 'completed')
    return first?.id ?? null
  }, [selectedRun, runsData])

  const { data: resultsData, isLoading: resultsLoading } =
    useDiscoveryResults(initialRun, 200)

  // Is there a run currently going? Drives the trigger button label/
  // disabled state without an extra round trip — `useDiscoveryRuns`
  // already refreshes every 60s, so this stays roughly live.
  const runningRun = runsData?.items.find((r) => r.status === 'running')

  const [triggering, setTriggering] = useState(false)
  const [triggerMsg, setTriggerMsg] = useState<string | null>(null)
  const [fullMode, setFullMode] = useState(false)

  const onTrigger = async () => {
    const confirmMsg = fullMode
      ? t('discovery.trigger_confirm_full', {
          defaultValue: 'Глубокое сканирование (DCA + Phase 2) — ожидаемое время 4-6 часов. Запустить?',
        })
      : t('discovery.trigger_confirm')
    if (!confirm(confirmMsg)) return
    setTriggering(true); setTriggerMsg(null)
    try {
      const r = await triggerDiscovery('daily', { full: fullMode })
      if (r.status === 'already_running') {
        setTriggerMsg(t('discovery.trigger_already', {
          id: r.in_flight_run_id,
          mins: r.in_flight_minutes?.toFixed(1) ?? '?',
        }))
      } else {
        setTriggerMsg(t('discovery.trigger_started', {
          cid: r.container_id ?? '?',
        }))
        // Bump the runs list so the new 'running' row shows up
        // without waiting for the next 60s tick.
        setTimeout(() => qc.invalidateQueries({ queryKey: ['discovery', 'runs'] }), 2000)
      }
    } catch (e) {
      const msg = (e as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail ?? String(e)
      setTriggerMsg(t('discovery.trigger_failed', { msg }))
    } finally {
      setTriggering(false)
    }
  }

  return (
    <div className="p-4 sm:p-6">
      <header className="mb-4 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('discovery.title')}
          </h1>
          <p className="text-sm text-textDim mt-1">{t('discovery.subtitle')}</p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-3 flex-wrap">
            {runningRun && (
              <span className="text-xs text-amber font-mono">
                {t('discovery.running_now', {
                  id: runningRun.id,
                  ago: timeAgo(runningRun.started_at),
                })}
              </span>
            )}
            {/* Full-mode toggle: includes DCA grid + Phase 2 alert
                filter search. Disabled while a run is in flight so the
                user can't change the mode mid-trigger. */}
            <label className="flex items-center gap-2 text-xs text-textDim font-mono cursor-pointer select-none">
              <input
                type="checkbox"
                checked={fullMode}
                disabled={triggering || !!runningRun}
                onChange={(e) => setFullMode(e.target.checked)}
                className="accent-fire"
              />
              {t('discovery.full_mode', { defaultValue: 'Глубокое (DCA + Phase 2, ~5ч)' })}
            </label>
            <Button
              size="sm"
              variant={runningRun ? 'secondary' : 'primary'}
              disabled={triggering || !!runningRun}
              onClick={onTrigger}
              title={t('discovery.trigger_tooltip')}
            >
              {triggering
                ? t('discovery.trigger_starting')
                : runningRun
                ? t('discovery.trigger_running')
                : fullMode
                ? t('discovery.trigger_button_full', { defaultValue: 'Запустить (полное)' })
                : t('discovery.trigger_button')}
            </Button>
          </div>
        )}
      </header>

      <PipelineCard />
      <FleetHealthCard />

      {triggerMsg && (
        <p className="text-xs text-textDim mb-3 font-mono">{triggerMsg}</p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
        {/* Runs sidebar */}
        <section className="bg-ink border border-line max-h-[80vh] overflow-y-auto">
          <header className="px-4 py-2 border-b border-line text-xs text-textDim uppercase tracking-wider">
            {t('discovery.runs')}
          </header>
          {runsLoading || !runsData ? (
            <div className="px-4 py-8 text-textFaint text-sm text-center">—</div>
          ) : runsData.items.length === 0 ? (
            <div className="px-4 py-8 text-textFaint text-sm text-center">
              {t('discovery.no_runs')}
            </div>
          ) : (
            <ul className="divide-y divide-line/40">
              {runsData.items.map((r) => (
                <li
                  key={r.id}
                  onClick={() => setSelectedRun(r.id)}
                  className={cn(
                    'px-4 py-3 cursor-pointer hover:bg-elevated/30 text-xs font-mono',
                    r.id === initialRun && 'bg-elevated/50 border-l-2 border-fire',
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">#{r.id}</span>
                    <span className={cn('pill text-2xs', runTypeClass(r.run_type))}>
                      {r.run_type}
                    </span>
                    <span className={cn('pill text-2xs ml-auto', statusClass(r.status))}>
                      {r.status}
                    </span>
                  </div>
                  <div className="mt-1 text-textFaint">
                    {timeAgo(r.started_at)}
                  </div>
                  <div className="mt-1 text-textDim">
                    {t('discovery.tested', {
                      variations: r.total_variations_tested,
                      found: r.total_strategies_found,
                    })}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Results table */}
        <section className="bg-ink border border-line">
          {initialRun == null ? (
            <div className="px-5 py-10 text-textFaint text-sm text-center">
              {t('discovery.pick_run')}
            </div>
          ) : resultsLoading || !resultsData ? (
            <div className="px-5 py-10 text-textFaint text-sm text-center">—</div>
          ) : resultsData.items.length === 0 ? (
            <div className="px-5 py-10 text-textFaint text-sm text-center">
              {t('discovery.no_results')}
            </div>
          ) : (
            <>
              <header className="px-5 py-2 border-b border-line text-xs text-textDim uppercase tracking-wider flex items-center gap-3">
                {t('discovery.results_for')} #{resultsData.run_id}
                <span className="ml-auto font-mono">
                  {t('discovery.results_count', { count: resultsData.items.length })}
                </span>
                {/* applied / total summary — surfaces how many of the
                    candidates were promoted to live trading_strategies
                    by the auto-apply step. */}
                {(() => {
                  const applied = resultsData.items.filter((r) => r.applied_strategy_id != null).length
                  return (
                    <span className="font-mono text-pulse">
                      {t('discovery.applied_count', {
                        applied,
                        total: resultsData.items.length,
                        defaultValue: `applied ${applied}/${resultsData.items.length}`,
                      })}
                    </span>
                  )
                })()}
              </header>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-textDim text-xs uppercase tracking-wider border-b border-line bg-elevated/40">
                    <tr>
                      <Th className="text-right px-3">#</Th>
                      <Th className="text-left">{t('discovery.col.move_type')}</Th>
                      <Th className="text-left">{t('discovery.col.tf')}</Th>
                      <Th className="text-left">{t('discovery.col.dir')}</Th>
                      <Th className="text-left">{t('discovery.col.regime')}</Th>
                      <Th className="text-left">{t('discovery.col.tier')}</Th>
                      <Th className="text-right">{t('discovery.col.tp_sl_hold')}</Th>
                      <Th className="text-right">{t('discovery.col.pnl_3d')}</Th>
                      <Th className="text-right">{t('discovery.col.pnl_10d')}</Th>
                      <Th className="text-right">{t('discovery.col.win_3d')}</Th>
                      <Th className="text-right">{t('discovery.col.trades_3d')}</Th>
                      <Th className="text-right">{t('discovery.col.weighted')}</Th>
                      <Th className="text-center">{t('discovery.col.applied')}</Th>
                      <Th className="text-left px-3">{t('discovery.col.recommendation')}</Th>
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    {resultsData.items.map((r) => (
                      <ResultRow key={r.id} r={r} />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  )
}

/* ─── Row ───────────────────────────────────────────────────────── */

function ResultRow({ r }: { r: DiscoveryResult }) {
  const { t } = useTranslation('app')
  const pnlTone = (v: number | null) =>
    v == null ? 'text-textFaint' : v >= 0 ? 'text-pulse' : 'text-rose'
  const recTone = recClass(r.recommendation)
  // move_type tells continuation vs reversal vs ml — colour-code so the
  // eye lands on the kind of trade variant immediately.
  const mt = r.move_type ?? r.signal_type
  // PULSE-50: ML direction predictor is off. Historical `*_ml` rows are
  // shown dimmed with an "ML off" tag so they're recognisable but no
  // longer look like real ML strategies.
  const isMlVariant = mt.endsWith('_ml')
  const mtClass = mt.includes('_reversal') ? 'text-rose'
                : isMlVariant              ? 'text-textFaint line-through'
                : mt.endsWith('_continuation') ? 'text-pulse'
                : 'text-text'
  return (
    <tr className={cn(
      'border-b border-line/40 hover:bg-elevated/30',
      isMlVariant && 'opacity-50',
    )}>
      <td className="text-right px-3 py-2 text-textFaint tabular-nums">{r.rank}</td>
      <td className={cn('py-2 font-semibold text-xs', mtClass)}>
        {mt}
        {isMlVariant && (
          <span
            className="ml-1 text-2xs text-rose normal-case no-underline"
            title={t('discovery.ml_disabled_tooltip')}
          >
            {t('discovery.ml_off')}
          </span>
        )}
      </td>
      <td className="py-2 text-textDim">{r.timeframe}</td>
      <td className="py-2 uppercase">
        {r.direction === 'long' ? <span className="text-pulse">L</span>
          : r.direction === 'short' ? <span className="text-rose">S</span>
          : <span className="text-sky">{r.direction ?? '—'}</span>}
      </td>
      <td className="py-2 text-xs">
        {r.regime_bucket ? (
          <span className={regimeClass(r.regime_bucket)}>
            {t(`dashboard.market.phase.${r.regime_bucket}`, { defaultValue: r.regime_bucket })}
          </span>
        ) : <span className="text-textFaint">—</span>}
      </td>
      <td className="py-2">
        {r.tier ? (
          <span className={cn('pill text-2xs', tierClass(r.tier))}>{r.tier}</span>
        ) : <span className="text-textFaint">—</span>}
      </td>
      <td className="text-right py-2 text-textDim tabular-nums">
        {r.take_profit_pct}/{r.stop_loss_pct}/{r.max_hold_minutes}m
      </td>
      <td className={cn('text-right py-2 tabular-nums', pnlTone(r.pnl_3d))}
          title={pnlTooltip(r.pnl_3d, r.trades_3d, '3д')}>
        {formatSumPct(r.pnl_3d)}
      </td>
      <td className={cn('text-right py-2 tabular-nums', pnlTone(r.pnl_10d))}
          title={pnlTooltip(r.pnl_10d, r.trades_10d, '10д')}>
        {formatSumPct(r.pnl_10d)}
      </td>
      <td className="text-right py-2 text-textDim tabular-nums">
        {/* win_rate_3d хранится как доля 0..1 → умножаем на 100. */}
        {r.win_rate_3d != null ? formatPct(r.win_rate_3d * 100, { digits: 0 }) : '—'}
      </td>
      <td className="text-right py-2 text-textDim tabular-nums">{r.trades_3d}</td>
      <td className={cn('text-right py-2 tabular-nums font-semibold',
        pnlTone(r.weighted_pnl))}>
        {r.weighted_pnl != null ? r.weighted_pnl.toFixed(2) : '—'}
      </td>
      <td className="text-center py-2">
        {r.applied_strategy_id != null ? (
          <span
            className="text-pulse font-mono text-2xs"
            title={t('discovery.applied_tooltip', {
              id: r.applied_strategy_id,
              defaultValue: `Applied as strategy #${r.applied_strategy_id}`,
            })}
          >
            ✓ #{r.applied_strategy_id}
          </span>
        ) : (
          <span className="text-textFaint">—</span>
        )}
      </td>
      <td className="px-3 py-2">
        <span className={cn('pill text-2xs', recTone)}>
          {t(`discovery.rec.${r.recommendation}`, { defaultValue: r.recommendation })}
        </span>
        {r.compared_to_strategy_id && (
          <span className="ml-1 text-2xs text-textFaint">
            vs #{r.compared_to_strategy_id}
            {r.improvement_pct != null && ` (${formatPct(r.improvement_pct, { sign: true, digits: 0 })})`}
          </span>
        )}
      </td>
    </tr>
  )
}

function Th({ className, children }: { className?: string; children: React.ReactNode }) {
  return <th className={cn('py-2 font-medium', className)}>{children}</th>
}

function runTypeClass(t: string): string {
  switch (t) {
    case 'daily':    return 'bg-pulse-faint text-pulse'
    case 'targeted': return 'bg-amber-faint text-amber'
    case 'probe':    return 'bg-sky-faint text-sky'
    default:         return 'bg-elevated text-textDim'
  }
}

function statusClass(s: string): string {
  switch (s) {
    case 'completed': return 'bg-elevated text-textDim'
    case 'running':   return 'bg-sky-faint text-sky'
    case 'failed':    return 'bg-rose-faint text-rose'
    case 'cancelled': return 'bg-elevated text-textFaint'
    default:          return 'bg-elevated text-textDim'
  }
}

function recClass(r: string): string {
  switch (r) {
    case 'replace_a':
    case 'replace_b': return 'bg-fire-faint text-fire'
    case 'new_pair':  return 'bg-amber-faint text-amber'
    case 'none':      return 'bg-elevated text-textFaint'
    default:          return 'bg-elevated text-textDim'
  }
}

function tierClass(t: string): string {
  switch (t.toUpperCase()) {
    case 'A': return 'bg-pulse-faint text-pulse'
    case 'B': return 'bg-amber-faint text-amber'
    case 'C': return 'bg-rose-faint text-rose'
    default:  return 'bg-elevated text-textDim'
  }
}

/**
 * Discovery stores PnL as the SUM of per-trade % returns over the window
 * (e.g. 193 trades each gaining/losing some % → pnl_total = sum). Showing
 * "+228.97%" reads as "228% over 3 days" which is wrong. Render as a
 * dimensionless sum with a "Σ" prefix; the tooltip gives the average
 * per-trade so it's unambiguous.
 */
function formatSumPct(v: number | null): string {
  if (v == null) return '—'
  const sign = v > 0 ? '+' : ''
  return `Σ ${sign}${v.toFixed(2)}`
}

function pnlTooltip(pnl: number | null, trades: number, window: string): string {
  if (pnl == null || !trades) return ''
  const avg = pnl / trades
  return `Сумма % PnL по ${trades} сделкам за ${window}. ` +
         `Среднее ${avg >= 0 ? '+' : ''}${avg.toFixed(2)}% / сделку.`
}

function regimeClass(r: string): string {
  switch (r) {
    case 'bullish':       return 'text-pulse'
    case 'bearish':       return 'text-rose'
    case 'consolidation': return 'text-amber'
    case 'recovery':      return 'text-sky'
    case 'capitulation':  return 'text-rose'
    default:              return 'text-textDim'
  }
}


// ─── PULSE-224/227: прозрачность ночного pipeline ────────────────────────

function verdictTone(v?: string | null) {
  if (!v) return 'text-textFaint'
  if (v.startsWith('PASS')) return 'text-mint'
  if (v.startsWith('FAIL')) return 'text-rose'
  return 'text-textFaint'
}

export function PipelineCard() {
  const { data } = usePipelineLatest()
  if (!data?.run) return null
  const run = data.run
  const byVerdict: Record<string, number> = {}
  for (const v of data.battery ?? []) {
    byVerdict[v.verdict] = (byVerdict[v.verdict] ?? 0) + Number(v.n)
  }
  return (
    <div className="mb-4 p-4 bg-ink border border-line">
      <div className="flex items-center gap-3 mb-2">
        <span className="text-2xs font-semibold uppercase tracking-wider text-textDim">
          Ночной pipeline · ран #{run.id}
        </span>
        <span className={cn('text-xs font-mono', run.status === 'completed' ? 'text-mint' : 'text-amber-400')}>
          {run.status}
        </span>
        <span className="text-xs text-textFaint">{timeAgo(run.completed_at ?? run.started_at)}</span>
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs mb-2">
        <span className="text-textFaint">Баттерея:</span>
        {Object.entries(byVerdict).map(([v, n]) => (
          <span key={v} className={cn('font-mono', verdictTone(v))}>{v}: {n}</span>
        ))}
        {Object.keys(byVerdict).length === 0 && <span className="text-textFaint">нет вердиктов</span>}
      </div>
      <div className="text-xs">
        <span className="text-textFaint mr-2">Применено ({(data.applied ?? []).length}):</span>
        {(data.applied ?? []).map((a: any) => (
          <span key={a.id} className="inline-flex items-center gap-1 mr-3 font-mono">
            <span className="text-text">{a.name}</span>
            <span className="text-textFaint">TP{a.tp}/SL{a.sl}/{a.hold}м</span>
            {a.phase_lock && <span className="px-1 bg-sky/15 text-sky rounded-sm">{JSON.parse(a.phase_lock).join(',')}</span>}
            {a.shock_lock && <span className="px-1 bg-rose/15 text-rose rounded-sm">{JSON.parse(a.shock_lock).join(',')}</span>}
          </span>
        ))}
        {(data.applied ?? []).length === 0 && <span className="text-textFaint">—</span>}
      </div>
    </div>
  )
}

export function FleetHealthCard() {
  const { data } = useFleetHealth()
  const fleet = data?.fleet ?? []
  if (!fleet.length) return null
  return (
    <div className="mb-4 bg-ink border border-line">
      <div className="px-4 pt-3 pb-2 text-2xs font-semibold uppercase tracking-wider text-textDim">
        Здоровье флота (battery-вердикт ночи × live 14д)
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-textFaint text-left">
              <th className="px-4 py-1 font-normal">Стратегия</th>
              <th className="px-2 py-1 font-normal">Замок</th>
              <th className="px-2 py-1 font-normal text-right">Battery</th>
              <th className="px-2 py-1 font-normal text-right">exBest</th>
              <th className="px-2 py-1 font-normal text-right">Live 14д</th>
              <th className="px-2 py-1 font-normal text-right">n</th>
            </tr>
          </thead>
          <tbody>
            {fleet.map((f: any) => (
              <tr key={f.id} className="border-t border-line/60">
                <td className="px-4 py-1 font-mono text-text">{f.name}</td>
                <td className="px-2 py-1">
                  {f.phase_lock && <span className="px-1 bg-sky/15 text-sky rounded-sm">{JSON.parse(f.phase_lock).join(',')}</span>}
                  {f.shock_lock && <span className="px-1 bg-rose/15 text-rose rounded-sm">{JSON.parse(f.shock_lock).join(',')}</span>}
                  {!f.phase_lock && !f.shock_lock && <span className="text-amber-400">без замка</span>}
                </td>
                <td className={cn('px-2 py-1 text-right font-mono', verdictTone(f.battery_verdict))}>
                  {f.battery_verdict ?? 'не проверена'}
                </td>
                <td className="px-2 py-1 text-right font-mono">
                  {f.ex_best_week != null ? Number(f.ex_best_week).toFixed(2) : '—'}
                </td>
                <td className={cn('px-2 py-1 text-right font-mono',
                  f.live_14d != null && Number(f.live_14d) < 0 ? 'text-rose' : 'text-mint')}>
                  {f.live_14d != null ? `${Number(f.live_14d) >= 0 ? '+' : ''}${Number(f.live_14d).toFixed(1)}` : '—'}
                </td>
                <td className="px-2 py-1 text-right font-mono text-textFaint">{f.live_n ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
